import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '@/api/client';
import type { Block, Outcome, ProgrammeResponse, Progression, ReportSummary, StickerKind, WeekSummary } from '@/api/types';

export type ExerciseBlock = Extract<Block, { kind: 'exercise' }>;

/**
 * Exercise blocks of a programme, keyed by exercise id (locked pages carry none), plus the
 * activities that joined after a goal was reached.
 */
export const exerciseBlocks = (programme: ProgrammeResponse['programme'], nextUp: ExerciseBlock[] = []): Map<string, ExerciseBlock> => {
  const map = new Map<string, ExerciseBlock>();
  for (const page of programme.pages) {
    if (page.locked) continue;
    for (const b of page.blocks) if (b.kind === 'exercise') map.set(b.exerciseId, b);
  }
  for (const b of nextUp) map.set(b.exerciseId, b);
  return map;
};

/** The activity an original programme activity became after goals (itself when none). */
export const currentActivity = (exerciseId: string, progression?: Progression): string => {
  const next = new Map((progression?.reached ?? []).map((g) => [g.exerciseId, g.nextExerciseId]));
  const seen = new Set<string>();
  let id = exerciseId;
  while (!seen.has(id) && next.get(id)) {
    seen.add(id);
    id = next.get(id)!;
  }
  return id;
};

/** The programme week the second-phase activities join in (the backend's SECOND_PHASE_FROM_WEEK). */
export const SECOND_PHASE_FROM_WEEK = 3;
/** Whether the family is doing this activity yet: the ones marked phase 2 wait for week 3. */
export const isActive = (a: { phase?: 1 | 2 } | undefined, week: Pick<WeekSummary, 'programmeWeek'>): boolean =>
  a?.phase !== 2 || week.programmeWeek >= SECOND_PHASE_FROM_WEEK;

/** The newest home assessment with an unlocked report — its programme is the one the family is using. */
export const latestUnlocked = (reports: ReportSummary[], childId?: string): ReportSummary | undefined =>
  [...reports]
    .filter((r) => r.status === 'unlocked' && (!childId || r.childId === childId))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];

export const doneToday = (week: WeekSummary, exerciseId: string) =>
  week.days.find((d) => d.date === week.today)?.logs.find((l) => l.exerciseId === exerciseId);

export interface ProgrammeState {
  data?: ProgrammeResponse;
  error?: string;
  loading: boolean;
  /** 403 from the API: the session exists but has not been unlocked. */
  locked: boolean;
  blocks: Map<string, ExerciseBlock>;
  saving: boolean;
  saveError?: string;
  reload: () => void;
  log: (exerciseId: string, outcome: Outcome, sticker?: StickerKind) => Promise<boolean>;
  unlog: (exerciseId: string) => Promise<boolean>;
  /** Marks an activity's goal as reached; resolves to the next activity's id, if the chain goes on. */
  reachGoal: (exerciseId: string) => Promise<{ ok: boolean; nextExerciseId?: string }>;
  undoGoal: (exerciseId: string) => Promise<boolean>;
}

export const useProgramme = (assessmentId: string | undefined): ProgrammeState => {
  const [data, setData] = useState<ProgrammeResponse>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(Boolean(assessmentId));
  const [locked, setLocked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!assessmentId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    api.programme
      .get(assessmentId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403) setLocked(true);
        else setError(err instanceof Error ? err.message : 'load_failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [assessmentId, nonce]);

  const withWeek = useCallback(async (run: () => Promise<{ week: WeekSummary; progression?: Progression }>) => {
    setSaving(true);
    setSaveError(undefined);
    try {
      const { week, progression } = await run();
      setData((d) => (d ? { ...d, week, ...(progression ? { progression } : {}) } : d));
      return true;
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'save_failed');
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  const log = useCallback(
    (exerciseId: string, outcome: Outcome, sticker?: StickerKind) =>
      assessmentId ? withWeek(() => api.programme.log(assessmentId, { exerciseId, outcome, sticker })) : Promise.resolve(false),
    [assessmentId, withWeek],
  );
  const unlog = useCallback(
    (exerciseId: string) =>
      assessmentId && data ? withWeek(() => api.programme.unlog(assessmentId, data.week.today, exerciseId)) : Promise.resolve(false),
    [assessmentId, data, withWeek],
  );

  const reachGoal = useCallback(
    async (exerciseId: string) => {
      if (!assessmentId) return { ok: false };
      let nextExerciseId: string | undefined;
      const ok = await withWeek(async () => {
        const res = await api.programme.reachGoal(assessmentId, exerciseId);
        nextExerciseId = res.goal.nextExerciseId;
        return res;
      });
      return { ok, nextExerciseId };
    },
    [assessmentId, withWeek],
  );
  const undoGoal = useCallback(
    (exerciseId: string) => (assessmentId ? withWeek(() => api.programme.undoGoal(assessmentId, exerciseId)) : Promise.resolve(false)),
    [assessmentId, withWeek],
  );

  const blocks = useMemo(
    () => (data ? exerciseBlocks(data.programme, data.progression?.nextUp) : new Map<string, ExerciseBlock>()),
    [data],
  );

  return { data, error, loading, locked, blocks, saving, saveError, reload: () => setNonce((n) => n + 1), log, unlog, reachGoal, undoGoal };
};
